import type {
  HoverIdleSnapshot,
  HovGainUpgradeMpTarget,
} from "@/features/hoverIdle/types";

export interface HovGainUpgradePurchaseSideEffectEvent {
  readonly target: HovGainUpgradeMpTarget;
  readonly isMilestone: boolean;
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter?: number;
  readonly snapshot: HoverIdleSnapshot;
}

export interface HovGainUpgradeMilestonePurchaseSideEffectEvent {
  readonly target: HovGainUpgradeMpTarget;
  readonly isMilestone: true;
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter?: number;
}

export interface HovGainUpgradePurchaseSideEffectBatcher {
  beginBurst(): void;
  endBurst(): void;
  recordSuccessfulPurchase(event: HovGainUpgradePurchaseSideEffectEvent): void;
  flush(snapshotOverride?: HoverIdleSnapshot): void;
  hasPending(): boolean;
}

export interface HovGainUpgradePurchaseSideEffectBatcherOptions {
  readonly refreshView: (snapshot: HoverIdleSnapshot) => void;
  readonly requestAutosave: () => void;
  readonly recordMilestonePurchases?: (
    purchases: readonly HovGainUpgradeMilestonePurchaseSideEffectEvent[],
    snapshot: HoverIdleSnapshot,
  ) => void;
}

export function createHovGainUpgradePurchaseSideEffectBatcher(
  options: HovGainUpgradePurchaseSideEffectBatcherOptions,
): HovGainUpgradePurchaseSideEffectBatcher {
  let burstDepth = 0;
  let pendingSnapshot: HoverIdleSnapshot | null = null;
  const pendingMilestonePurchases: HovGainUpgradeMilestonePurchaseSideEffectEvent[] =
    [];

  const flush = (snapshotOverride?: HoverIdleSnapshot): void => {
    if (pendingSnapshot === null) {
      return;
    }
    const snapshot = snapshotOverride ?? pendingSnapshot;
    const milestonePurchases = pendingMilestonePurchases.splice(0);
    pendingSnapshot = null;

    options.refreshView(snapshot);
    if (milestonePurchases.length > 0) {
      options.recordMilestonePurchases?.(milestonePurchases, snapshot);
    }
    options.requestAutosave();
  };

  return {
    beginBurst(): void {
      burstDepth += 1;
    },
    endBurst(): void {
      if (burstDepth <= 0) {
        return;
      }
      burstDepth -= 1;
      if (burstDepth === 0) {
        flush();
      }
    },
    recordSuccessfulPurchase(
      event: HovGainUpgradePurchaseSideEffectEvent,
    ): void {
      pendingSnapshot = event.snapshot;
      if (event.isMilestone) {
        pendingMilestonePurchases.push({
          target: event.target,
          isMilestone: true,
          enhancementLevelBefore: event.enhancementLevelBefore,
          ...(event.enhancementLevelAfter !== undefined
            ? { enhancementLevelAfter: event.enhancementLevelAfter }
            : {}),
        });
      }
      if (burstDepth === 0) {
        flush();
      }
    },
    flush,
    hasPending(): boolean {
      return pendingSnapshot !== null;
    },
  };
}
